// ============================================
// CLASS FOR MANAGING PPT IMAGES
// ============================================

import { IconVariant } from "@/features/files/types";
import { IMAGE_PATHS } from "./constants";

export async function imageToBase64(imagePath: string): Promise<string | null> {
    try {
        const response = await fetch(imagePath);
        if (!response.ok) {
            console.warn(`Failed to load image: ${imagePath}`);
            return null;
        }

        const blob = await response.blob();

        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64 = reader.result as string;
                resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    } catch (error) {
        console.error(`Error loading image ${imagePath}:`, error);
        return null;
    }
}


export function getTechnologyIcon(technology: string, variant: string): string | null {
    return IMAGE_PATHS.technologies[variant][technology] || null;
}

/**
 * Preload ONLY the logos (front and back cover)
 */
async function preloadLogos(manager: PPTImageManager): Promise<void> {
    await manager.preloadMultiple([
        { key: 'logo-white', path: IMAGE_PATHS.logos.white },
        { key: 'logo-black', path: IMAGE_PATHS.logos.black },
        { key: 'folder', path: IMAGE_PATHS.icons.folder }
    ]);
}

async function preloadIcons(manager: PPTImageManager): Promise<void> {
    await manager.preloadMultiple([
      { key: 'folder', path: IMAGE_PATHS.icons.folder },
      { key: 'cable', path: IMAGE_PATHS.icons.cable },
      { key: 'info', path: IMAGE_PATHS.icons.info },
      { key: 'list', path: IMAGE_PATHS.icons.list },
      { key: 'settings', path: IMAGE_PATHS.icons.settings },
      { key: 'land', path: IMAGE_PATHS.icons.land },
      { key: 'globe', path: IMAGE_PATHS.icons.globe },
      { key: 'mail', path: IMAGE_PATHS.icons.mail },
      { key: 'click', path: IMAGE_PATHS.icons.click },
      { key: 'pie', path: IMAGE_PATHS.icons.pie },
      { key: 'chart', path: IMAGE_PATHS.icons.chart },
      { key: 'users', path: IMAGE_PATHS.icons.users },
    ]);
}

/**
 * Preload ONLY the advisor images (4 images)
 */
async function preloadAdvisors(manager: PPTImageManager): Promise<void> {
    await manager.preloadMultiple([
        { key: 'advisor-legal', path: IMAGE_PATHS.advisors.legal },
        { key: 'advisor-financial', path: IMAGE_PATHS.advisors.financial },
        { key: 'advisor-technical', path: IMAGE_PATHS.advisors.technical },
        { key: 'advisor-epc', path: IMAGE_PATHS.advisors.epc },
    ]);
}

/**
 * Pre-loads ONLY the icons for the technologies included in the project.
 * @param manager - Manager instance.
 * @param technologies - Array of technologies in the project.
 * @param variant - Indicates which type of icon will be loaded: green or black.
 */
async function preloadProjectTechnologyIcons(
    manager: PPTImageManager,
    technologies: Array<string>,
    variant: IconVariant
): Promise<void> {

    const imagesToLoad = technologies
        .map(tech => {
            const iconPath = getTechnologyIcon(tech, variant);
            if (!iconPath) return null;

            return {
                key: `icon-${tech}-${variant}`,
                path: iconPath
            };
        })
        .filter(Boolean) as Array<{ key: string; path: string }>;

    await manager.preloadMultiple(imagesToLoad);
}

/**
 * Centralized image manager for PPT
 * Pre-loads the necessary images before generating the PPT
 */
export class PPTImageManager {
    private images: Map<string, string> = new Map();
    private loading: Set<string> = new Set();

    /**
     * Pre-loads an image by its path
     * @param key - Unique identifier for the image
     * @param path - Image path
     */
    async preload(key: string, path: string): Promise<void> {
        if (this.images.has(key) || this.loading.has(key)) {
            return;
        }

        this.loading.add(key);
        const base64 = await imageToBase64(path);

        if (base64) {
            this.images.set(key, base64);
        }

        this.loading.delete(key);
    }

    /**
     * Pre-load multiple images at once
     * @param imagesToLoad - Array of {key, path}
     */
    async preloadMultiple(imagesToLoad: Array<{ key: string; path: string }>): Promise<void> {
        const results = await Promise.allSettled(
            imagesToLoad.map(({ key, path }) => this.preload(key, path))
        );

        // Log of failed images
        results.forEach((result, index) => {
            if (result.status === 'rejected') {
                console.warn(`Failed to load image: ${imagesToLoad[index].key}`);
            }
        });
    }

    /**
     * Gets an already uploaded image.
     * @param key - Image identifier.
     * @returns Base64 of the image or null if it does not exist.
     */
    get(key: string): string | null {
        return this.images.get(key) || null;
    }

    /**
     * Checks if an image is loaded
     * @param key - Image identifier
     */
    has(key: string): boolean {
        return this.images.has(key);
    }

    /**
     * Clears all loaded images (useful for freeing up memory)
     */
    clear(): void {
        this.images.clear();
        this.loading.clear();
    }

    /**
     * Gets the current cache size
     */
    get size(): number {
        return this.images.size;
    }
}


/**
 * Optimal strategy: only load what you need for the project
 * @param manager - Manager instance
 * @param project - Project data
 */
export async function preloadImagesForProject(
    manager: PPTImageManager,
    project: {
        technologies?: any;
    }
): Promise<void> {

    // 1. Always upload logos (front and back cover + content)
    await preloadLogos(manager);
    await preloadIcons(manager);

    // 2. Load only icons for technologies that this project has
    if (project.technologies && project.technologies.length > 0) {
        await preloadProjectTechnologyIcons(manager, project?.technologies, 'green');
        await preloadProjectTechnologyIcons(manager, project?.technologies, 'black');
    }

    // 3. Load advisors images
    await preloadAdvisors(manager);
}
